import React, { useState } from 'react';
import { ChevronDown, ChevronRight, X, Plus } from 'lucide-react';
import {
  MT_MASTERY_LEVELS,
  MT_TECHNIQUE_TYPES,
  MT_RANGES,
  MT_COMMON_TECHNIQUES,
} from '../../data/combativesMT';
import SelectWithOther from './SelectWithOther';

/**
 * MTTechniqueRow — single technique entry in the Muay Thai session log.
 *
 * Shape mirrors BJJ's TechniqueRow but with Muay Thai vocabulary:
 *   - type (Punch / Kick / Knee / Elbow / Clinch / Defense / Combination)
 *   - range (Long / Mid / Close / Clinch) — striking's analogue to BJJ's
 *     position taxonomy
 *   - name + autocomplete from history + starter common-technique list
 *   - detail + reps + mastery
 *
 * No weapons toggle (irrelevant for Muay Thai), no submission field
 * (irrelevant), no step-by-step editor (overkill for a strike — the
 * detail freeform handles it).
 */
const MTTechniqueRow = ({ value, onChange, onRemove, index, nameSuggestions = [] }) => {
  const [expanded, setExpanded] = useState(true);
  const summary = [value.range, value.type, value.name].filter(Boolean).join(' · ') || 'New technique';
  const set = (patch) => onChange({ ...value, ...patch });

  const mergedSuggestions = Array.from(new Set([...nameSuggestions, ...MT_COMMON_TECHNIQUES])).sort();

  const selectClass = 'w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg';
  const labelClass = 'block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1';

  return (
    <div className="bg-enf-surface-mid border border-enf-surface-border rounded-lg overflow-hidden">
      {/* Collapsed header */}
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          data-testid={`mt-technique-expand-${index}`}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          {expanded ? <ChevronDown className="w-4 h-4 text-enf-text-muted flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-enf-text-muted flex-shrink-0" />}
          <span className="text-sm font-semibold text-enf-text-primary truncate">{summary}</span>
        </button>
        {value.mastery && (
          <span className="px-1.5 py-0.5 rounded bg-enf-surface-deep border border-enf-surface-border font-mono text-[10px] text-enf-text-secondary flex-shrink-0">
            {value.mastery}
          </span>
        )}
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove technique"
          data-testid={`mt-technique-remove-${index}`}
          className="text-enf-text-muted hover:text-enf-role-warning p-1 -m-1 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-enf-surface-border pt-3">
          {/* Type + Range row */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>Type</label>
              <select value={value.type} onChange={(e) => set({ type: e.target.value })}
                data-testid={`mt-technique-${index}-type`} className={selectClass}>
                <option value="">&mdash; pick &mdash;</option>
                {MT_TECHNIQUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Range</label>
              <select value={value.range} onChange={(e) => set({ range: e.target.value })}
                data-testid={`mt-technique-${index}-range`} className={selectClass}>
                <option value="">&mdash; pick &mdash;</option>
                {MT_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          {/* Name with autocomplete — SelectWithOther wraps the merged
              suggestions (curated MT_COMMON_TECHNIQUES + user history). */}
          <div>
            <label className={labelClass}>Technique name</label>
            <SelectWithOther
              value={value.name}
              onChange={(name) => set({ name })}
              suggestions={mergedSuggestions}
              placeholder="Select or type a technique…"
              testId={`mt-technique-${index}-name`}
            />
          </div>

          {/* Detail */}
          <div>
            <label className={labelClass}>Notes / cues (optional)</label>
            <textarea
              value={value.detail || ''}
              onChange={(e) => set({ detail: e.target.value })}
              rows={2}
              placeholder="What the coach called, what to focus on"
              data-testid={`mt-technique-${index}-detail`}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none"
            />
          </div>

          {/* Reps + Mastery row */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>Reps (optional)</label>
              <input type="text" inputMode="numeric" value={value.reps || ''}
                onChange={(e) => set({ reps: e.target.value })}
                placeholder="e.g. 20"
                data-testid={`mt-technique-${index}-reps`}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
            </div>
            <div>
              <label className={labelClass}>Mastery</label>
              <select value={value.mastery || 'Drilling'}
                onChange={(e) => set({ mastery: e.target.value })}
                data-testid={`mt-technique-${index}-mastery`} className={selectClass}>
                {MT_MASTERY_LEVELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <p className="text-[10px] text-enf-text-muted leading-snug">
            {MT_MASTERY_LEVELS.find((m) => m.value === value.mastery)?.help}
          </p>
        </div>
      )}
    </div>
  );
};

export default MTTechniqueRow;
